import { useState, useRef, ChangeEvent } from 'react';
import { Upload, X, FileText, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Card } from '@/components/ui/card';

interface DocumentUploadProps {
  orderId: string;
  onUpload: (file: File) => void;
  isUploading: boolean;
}

export const DocumentUpload = ({ orderId, onUpload, isUploading }: DocumentUploadProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      alert('Format non supporté. Utilisez JPG, PNG ou PDF.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Le fichier est trop volumineux (max 10MB)');
      return;
    }

    setSelectedFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      onUpload(selectedFile);
      // Reset after upload
      setSelectedFile(null);
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      {/* File Input */}
      <div className="flex items-center gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          onChange={handleFileSelect}
          disabled={isUploading}
          className="hidden"
          id={`file-input-${orderId}`}
        />
        <label
          htmlFor={`file-input-${orderId}`}
          className="flex-1 cursor-pointer"
        >
          <div className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-[#DBE2EA] rounded-lg hover:border-[#0880AE] transition-colors bg-[#EBF4F8] hover:bg-white">
            <Upload size={20} className="text-[#0880AE]" />
            <span className="text-sm font-medium text-[#2D3748] font-['Funnel_Sans']">
              {selectedFile ? selectedFile.name : 'Choisir un fichier'}
            </span>
          </div>
        </label>
        {selectedFile && !isUploading && (
          <Button
            onClick={handleClear}
            variant="ghost"
            size="icon"
            className="flex-shrink-0"
          >
            <X size={20} />
          </Button>
        )}
      </div>

      {/* Preview */}
      {selectedFile && (
        <Card className="p-4 border border-[#DBE2EA]">
          <div className="flex items-start gap-4">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Preview"
                className="w-24 h-24 object-cover rounded-lg"
              />
            ) : (
              <div className="w-24 h-24 bg-[#EBF4F8] rounded-lg flex items-center justify-center">
                <FileText size={32} className="text-[#0880AE]" />
              </div>
            )}
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#2D3748] font-['Funnel_Sans']">
                {selectedFile.name}
              </p>
              <p className="text-xs text-[#5A6B7A] font-['Funnel_Sans'] mt-1">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
              <p className="text-xs text-[#5A6B7A] font-['Funnel_Sans']">
                {selectedFile.type}
              </p>
            </div>
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div className="mt-4 space-y-2">
              <Progress value={66} className="h-2" />
              <p className="text-xs text-[#5A6B7A] font-['Funnel_Sans']">
                Upload en cours...
              </p>
            </div>
          )}

          {/* Upload Button */}
          {!isUploading && (
            <Button
              onClick={handleUpload}
              className="w-full mt-4 bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold"
            >
              <Upload size={18} className="mr-2" />
              Uploader
            </Button>
          )}
        </Card>
      )}

      {/* Info */}
      <p className="text-xs text-[#5A6B7A] font-['Funnel_Sans']">
        Formats acceptés: JPG, PNG, PDF (max 10MB)
      </p>
    </div>
  );
};
